'use client';

import { useTranslations } from 'next-intl';
import { useEffect } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { trackingManager } from '@/lib/tracking/tracking-manager-instance';
import { isChunkLoadError, reloadOnceForChunkError } from '@/lib/utils/chunk-error';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations('common.errorBoundary');
  const isChunkError = isChunkLoadError(error);

  useEffect(() => {
    // Capture the error with PostHog
    trackingManager.captureException(error, {
      error_boundary: 'route',
      error_digest: error.digest,
      timestamp: new Date().toISOString(),
    });
  }, [error]);

  useEffect(() => {
    // A rejected chunk stays rejected until the document reloads, so reset() cannot recover.
    if (isChunkError) reloadOnceForChunkError();
  }, [isChunkError]);

  return (
    <div className="min-h-safe-screen flex items-center justify-center bg-gray-50">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">{t('description')}</p>
          <Button
            onClick={() => (isChunkError ? window.location.reload() : reset())}
            className="w-full"
          >
            {t('tryAgain')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
